<template>
  <router-link
    :to="{ name: routePathName, params: { inspectionId: inspection.id, scope: inspectionScope } }"
    class="inspection-card"
    activeClass="inspection-card--active"
  >
    <div class="inspection-card__body" ref="card">
      <div class="inspection-card__header">
        <p class="typo-ignore typo-small-title inspection-card__title">
          {{ inspection.inspected_by && inspection.inspected_by.full_name }}
          <span v-if="inspection.inspection_type" class="inspection-card__type">{{ inspectionTypeText }}</span>
        </p>
        <!-- Inspection status -->
        <p :class="`typo-ignore typo-small-title inspection-card__status inspection-card__status--${inspection.status}`">
          <PrEngBadge class="icon" v-if="inspection.professional_engineer" />
          <component :is="statusIcon[inspection.status]" />
        </p>
      </div>
      <!-- Short Id / Issue count -->
      <div class="inspection-card__info">
        <p class="typo-ignore typo-tiny inspection-card__id">
          #{{ inspection.id && shortId }}
          <span class="inspection-card__issues" v-if="inspection.issues">
            {{
              pluralize(
                inspection.issues.length,
                $t("activerecord.models.ships/issue.one"),
                $t("activerecord.models.ships/issue.other")
              )
            }}
          </span>
        </p>
        <p class="typo-ignore typo-tiny inspection-card__date">
          {{ formatDate(inspection.date) }}
        </p>
      </div>
      <!-- Issue dots, modified in colour using rating level -->
      <div class="inspection-card__issue-summary" v-if="inspection.issues">
        <div
          v-for="(level, index) in riskRatingLevels"
          :key="index"
          :class="`inspection-card__issue inspection-card__issue--${level}`"
        ></div>
      </div>
    </div>
  </router-link>
</template>

<script setup>
import { computed, ref, watchEffect } from 'vue';
import I18n from 'i18n-js';
import { useRoute } from 'vue-router';

// Utils
import pluralize from '@/functions/pluralize';
import { renderShortId } from '@/functions/utils.js';
import getRiskRatingLevel from '@/functions/simm/get_risk_rating_level';
import { isInViewport } from '@/functions/utils';
import { formatDate } from '@/functions/date_format_helpers';

// Icons
import PrEngBadge from '@/images/rosette_16.svg';
import CircleCheckmark16Svg from '@/images/circle-checkmark-16.svg';
import ViewFinderDot16Svg from '@/images/viewfinder-dot-16.svg';
import CircleDot16Svg from '@/images/circle-dotted-16.svg';

const props = defineProps({
  inspection: { type: Object, required: true },
  pathName: { type: String, required: false, default: null },
});

const route = useRoute();

/**
 * Status icons
 */
const statusIcon = {
  approved: CircleCheckmark16Svg,
  in_progress: ViewFinderDot16Svg,
  awaiting_approval: CircleDot16Svg,
};

const card = ref(null);

/**
 * Returns I18n text for inspection types
 */
const inspectionTypeText = computed(() => {
  return I18n.t(
    `inspection_types.${props.inspection.inspection_type}.name`
  );
});

const inspectionScope = computed(() => {
  return props.inspection.status === 'approved' ? 'approved_inspections' : 'inspections';
});

const shortId = computed(() => renderShortId(props.inspection.id));

const riskRatingLevels = computed(() => {
  return props.inspection.issues
    .map((i) => i.risk_rating)
    .sort((a, b) => b - a)
    .map(getRiskRatingLevel);
});

const routePathName = computed(() => {
  return props.pathName || 'shipInspection';
});

watchEffect(() => {
  if (card.value && route.name === 'inspection' && route.params.inspectionId === props.inspection.id) {
    if (!isInViewport(card.value)) {
      card.value.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }
});
</script>
